import { Canvas } from '@react-three/fiber';
import { Physics } from '@react-three/rapier';
import { Sky, Stars, Cloud } from '@react-three/drei';
import { Suspense, useEffect, useState, useRef } from 'react';
import * as Colyseus from 'colyseus.js';
import { useControls, button, Leva } from 'leva';
import { EffectComposer, Bloom } from '@react-three/postprocessing';

// import { Arrow } from './Arrow';
// import { MagicProjectile } from './MagicProjectile';
import { useStore } from '../../store/useStore';
// import { useAudio } from '../../hooks/useAudio';
import { sfx } from '../../utils/SoundManager';
import { World } from './World';
import { Interior } from './Interior';
import { Arena } from './Arena';
import type { MyRoomState, Player as PlayerSchema } from '../../schema/GameState';
import { Chat } from '../ui/Chat';
import { isMobile } from '../../utils/isMobile';

export const Scene = () => {
    // Room State
    const [room, setRoom] = useState<Colyseus.Room<MyRoomState> | null>(null);
    const [remotePlayers, setRemotePlayers] = useState<Map<string, PlayerSchema>>(new Map());
    const remoteUpdateScheduled = useRef(false);

    // UI State (Leva)
    const { physicsDebug, timeOfDay } = useControls('Debug', {
        physicsDebug: false,
        timeOfDay: {
            value: new Date().getHours() + new Date().getMinutes() / 60,
            min: 0,
            max: 24,
            step: 0.1
        },
        theme: {
            value: 'cyberpunk',
            options: ['medieval', 'cyberpunk', 'manual_wizard', 'manual_archer'],
            onChange: (value) => useStore.getState().setTheme(value)
        },
        'Toggle Audio': button(() => useStore.getState().toggleAudio())
    });

    // Debug hash reactive state
    const [showDebugHash, setShowDebugHash] = useState(typeof window !== 'undefined' && window.location.hash.includes('debug'));
    useEffect(() => {
        const onHash = () => setShowDebugHash(window.location.hash.includes('debug'));
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    // Audio Init
    // const { playBgm } = useAudio();
    useEffect(() => {
        // playBgm('bgm_medieval'); // Auto-play might be blocked, but we try.
    }, []);

    // Day/Night Logic
    const sunPosition = [
        Math.cos((timeOfDay / 24) * Math.PI * 2) * 100,
        Math.sin((timeOfDay / 24) * Math.PI * 2) * 100,
        50
    ] as [number, number, number];

    const ambientIntensity = timeOfDay > 6 && timeOfDay < 18 ? 0.5 : 0.1;
    const dirIntensity = timeOfDay > 6 && timeOfDay < 18 ? 1.5 : 0;
    // Theme selector (avoid direct getState() in JSX)
    const theme = useStore((s) => s.theme);

    // Scene Management
    const currentScene = useStore((state) => state.currentScene);
    const setCurrentScene = useStore((state) => state.setCurrentScene);

    // Sync Scene to Server
    useEffect(() => {
        if (room && currentScene) {
            room.send("sceneChange", { scene: currentScene });
        }
    }, [room, currentScene]);

    // Handle ambient audio based on current scene
    useEffect(() => {
        if (currentScene === 'world') {
            // Play forest ambiance in the world
            sfx.playForestAmbiance();
        } else {
            // Stop ambiance in other scenes
            sfx.stopAll();
        }
    }, [currentScene]);


    useEffect(() => {
        // Production: Use VITE_SERVER_URL env var
        // Development: Use localhost
        const serverUrl = import.meta.env.VITE_SERVER_URL;

        let client: Colyseus.Client;

        if (serverUrl) {
            // Production: Use environment variable (e.g., wss://api.yourdomain.com)
            client = new Colyseus.Client(serverUrl);
        } else {
            // Development: Auto-detect protocol and use same host
            const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
            const host = window.location.hostname;
            const port = '2567';
            client = new Colyseus.Client(`${protocol}://${host}:${port}`);
        }

        let currentRoom: Colyseus.Room<MyRoomState> | null = null;
        let isUnmounted = false;

        async function connect() {
            try {
                // Join the room
                const r = await client.joinOrCreate<MyRoomState>("my_room");

                // If component unmounted while connecting, leave immediately
                if (isUnmounted) {
                    r.leave();
                    return;
                }

                console.log("Joined room:", r.sessionId);
                currentRoom = r;
                setRoom(r);

                // Initialize existing players
                if (r.state.players) {
                    r.state.players.forEach((player: PlayerSchema, sessionId: string) => {
                        setRemotePlayers(prev => new Map(prev).set(sessionId, player));
                    });
                }

                const scheduleRemotePlayersUpdate = () => {
                    if (remoteUpdateScheduled.current) return;
                    remoteUpdateScheduled.current = true;
                    requestAnimationFrame(() => {
                        remoteUpdateScheduled.current = false;
                        setRemotePlayers(new Map(r.state.players));
                    });
                };

                // Listen for new players
                r.state.players.onAdd((player: PlayerSchema, sessionId: string) => {
                    console.log("Player joined:", sessionId);

                    // Trigger re-render when ANY property changes (like currentScene)
                    player.listen("x", () => scheduleRemotePlayersUpdate());
                    player.listen("y", () => scheduleRemotePlayersUpdate());
                    player.listen("z", () => scheduleRemotePlayersUpdate());
                    player.listen("rx", () => scheduleRemotePlayersUpdate());
                    player.listen("currentScene", () => {
                        scheduleRemotePlayersUpdate();
                    });

                    setRemotePlayers(prev => {
                        const newMap = new Map(prev);
                        newMap.set(sessionId, player);
                        return newMap;
                    });
                });

                // Listen for players leaving
                r.state.players.onRemove((_: PlayerSchema, sessionId: string) => {
                    console.log("Player left:", sessionId);
                    setRemotePlayers(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(sessionId);
                        return newMap;
                    });
                });

                r.onMessage("itemCollected", (payload: { itemId: string; name: string; description?: string }) => {
                    const { addToInventory, updateQuestProgress, addStat } = useStore.getState();
                    addToInventory({ id: payload.itemId, name: payload.name });
                    updateQuestProgress('collect', payload.name, 1);
                    addStat('collect', 1, payload.name);
                });

            } catch (e) {
                console.error("Join error:", e);
            }
        }

        connect();

        return () => {
            isUnmounted = true;
            if (currentRoom) {
                console.log("Leaving room...");
                currentRoom.leave();
            }
        };
    }, []);

    // Resume AudioContext on first interaction
    useEffect(() => {
        const resumeAudio = () => {
            // Try Howler specific resume
            const win = window as Window & { Howler?: { ctx?: { state?: string; resume?: () => Promise<void>; }; }; };
            if (win.Howler) {
                const Howler = win.Howler;
                if (Howler.ctx && Howler.ctx.state === 'suspended' && typeof Howler.ctx.resume === 'function') {
                    console.log("[AUDIO] Attempting to resume AudioContext...");
                    Howler.ctx.resume().then(() => {
                        console.log("[AUDIO] AudioContext resumed successfully.");
                    }).catch((err: unknown) => {
                        console.error("[AUDIO] Resume failed:", err);
                    });
                }
            }
        };
        window.addEventListener('click', resumeAudio);
        window.addEventListener('mousedown', resumeAudio);
        window.addEventListener('touchstart', resumeAudio);
        window.addEventListener('keydown', resumeAudio);
        return () => {
            window.removeEventListener('click', resumeAudio);
            window.removeEventListener('mousedown', resumeAudio);
            window.removeEventListener('touchstart', resumeAudio);
            window.removeEventListener('keydown', resumeAudio);
        };
    }, []);

    // Bloom Settings
    const bloomProps = useControls('Post Processing', {
        intensity: { value: 1.5, min: 0, max: 10 },
        luminanceThreshold: { value: 1, min: 0, max: 2 },
        luminanceSmoothing: { value: 0.9, min: 0, max: 1 },
        mipmapBlur: true
    });

    return (
        <>
            {/* Only show Leva if #debug is present */}
            <div style={{ display: showDebugHash ? 'block' : 'none' }}>
                <Leva
                    hidden={!showDebugHash}
                    collapsed={isMobile()}
                    fill={false}
                    titleBar={{ title: 'Controls', drag: true }}
                    theme={{ colors: { highlight1: '#f09' } }}
                />
            </div>
            <Chat room={room} />
            <Canvas id="gl-canvas" shadows dpr={isMobile() ? [1, 1] : [1, 2]} camera={{ position: [0, 5, 10], fov: 50 }}>
                <Suspense fallback={null}>
                        {/* Environment */}
                        {theme === 'cyberpunk' ? (
                            <>
                                <color attach="background" args={['#050510']} />
                                <Stars radius={100} depth={50} count={isMobile() ? 800 : 5000} factor={4} saturation={0} fade speed={1} />
                                <ambientLight intensity={0.5} color="#00ffff" />
                                <pointLight position={[10, 10, 10]} intensity={1} color="#ff00ff" />
                                <Cloud opacity={0.3} speed={0.2} segments={20} position={[0, 20, -50]} color="#201040" />
                            </>
                        ) : (
                            <>
                                {/* Only render Sky during DAY or TWILIGHT */}
                                {timeOfDay > 5 && timeOfDay < 19 && (
                                    <Sky sunPosition={sunPosition} turbidity={10} rayleigh={0.5} mieCoefficient={0.005} mieDirectionalG={0.8} />
                                )}
                                <ambientLight intensity={timeOfDay > 6 && timeOfDay < 18 ? ambientIntensity : 0.1} />
                                <directionalLight
                                    position={sunPosition}
                                    intensity={dirIntensity}
                                    castShadow
                                    shadow-mapSize={[1024, 1024]}
                                    shadow-bias={-0.001}
                                />
                                {timeOfDay < 6 || timeOfDay > 18 ? (
                                    <>
                                        <color attach="background" args={['#02020a']} />
                                        <Stars radius={100} depth={50} count={isMobile() ? 800 : 5000} factor={4} saturation={0} fade speed={1} />
                                        <fog attach="fog" args={['#02020a', 10, 100]} />
                                    </>
                                ) : null}
                                <Cloud opacity={0.5} speed={0.4} segments={20} position={[0, 20, -50]} />
                                <Cloud opacity={0.5} speed={0.4} segments={20} position={[-50, 20, 0]} />
                            </>
                        )}

                        {/* Physics World */}
                        <Physics gravity={[0, -9.81, 0]} debug={physicsDebug}>
                            {currentScene === 'world' ? (
                                <World room={room} remotePlayers={remotePlayers} />
                            ) : currentScene === 'arena' ? (
                                <Arena onExit={() => setCurrentScene('world')} room={room} remotePlayers={remotePlayers} />
                            ) : (
                                <Interior type={currentScene} onExit={() => setCurrentScene('world')} />
                            )}
                        </Physics>

                    <EffectComposer>
                        <Bloom
                            luminanceThreshold={bloomProps.luminanceThreshold}
                            mipmapBlur={bloomProps.mipmapBlur}
                            intensity={bloomProps.intensity}
                            luminanceSmoothing={bloomProps.luminanceSmoothing}
                        />
                    </EffectComposer>
                </Suspense>
            </Canvas>
        </>
    );
};
